import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import httpHelper, { Method_TYPE } from 'utils/http-helper';
import URL_CONSTANTS from 'constants/url-constants';

const REFRESH_INTERVAL_MS = 10000;

const AllocationStats = () => {
  const navigate = useNavigate();
  const [deploymentPoints, setDeploymentPoints] = useState([]);
  const [batchName, setBatchName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isInitialLoad = useRef(true);

  useEffect(() => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      navigate('/login-screen');
    }
  }, [navigate]);

  const fetchSummary = useCallback(async () => {
    try {
      if (!isInitialLoad.current) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }
      setError('');

      const response = await httpHelper(URL_CONSTANTS.ALLOCATION_SUMMARY, Method_TYPE.GET);
      const list = Array.isArray(response?.data) ? response.data : [];

      const sorted = [...list].sort((a, b) => {
        const sectorCompare = (a?.sector?.name || '').localeCompare(
          b?.sector?.name || '',
          undefined,
          { sensitivity: 'base' }
        );
        if (sectorCompare !== 0) return sectorCompare;
        return (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' });
      });

      setDeploymentPoints(sorted);
      setBatchName(response?.batch?.name || '');
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching allocation summary:', err);
      setError('Unable to load allocation stats. Retrying...');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
      isInitialLoad.current = false;
    }
  }, []);

  useEffect(() => {
    fetchSummary();
    const intervalId = setInterval(fetchSummary, REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [fetchSummary]);

  const totals = deploymentPoints.reduce(
    (acc, point) => {
      acc.allocated += Number(point.allocatedCount) || 0;
      acc.required += Number(point.reqCount) || 0;
      return acc;
    },
    { allocated: 0, required: 0 }
  );

  const getFillPercent = (allocated, required) => {
    if (!required || required <= 0) return 0;
    return Math.min(100, Math.round((allocated / required) * 100));
  };

  const getStatusColor = (allocated, required) => {
    if (!required) return 'bg-muted';
    const ratio = allocated / required;
    if (ratio >= 1) return 'bg-success';
    if (ratio >= 0.6) return 'bg-warning';
    return 'bg-error';
  };

  const formatTime = (date) => {
    if (!date) return '—';
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <>
      <Helmet>
        <title>Allocation Stats - Tamil Nadu Prasanthi Seva</title>
        <meta
          name="description"
          content="Live deployment point allocation statistics for Tamil Nadu Prasanthi Seva"
        />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Breadcrumb />

        <main className="pt-8 pb-10">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h1 className="font-heading font-semibold text-2xl text-foreground">
                  Allocation Stats
                </h1>
                <p className="text-muted-foreground mt-1">
                  Live deployment point allocation overview
                  {batchName ? ` · ${batchName}` : ''}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <div className="bg-card border border-border rounded-lg px-4 py-2 text-sm">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Allocated:</span>
                      <span className="font-semibold text-success">{totals.allocated}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Required:</span>
                      <span className="font-semibold text-warning">{totals.required}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span
                    className={`inline-block w-2 h-2 rounded-full ${
                      isRefreshing ? 'bg-primary animate-pulse' : 'bg-success'
                    }`}
                  />
                  <span>Auto-refresh 10s</span>
                  <span>·</span>
                  <span>Updated {formatTime(lastUpdated)}</span>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  iconName="RefreshCw"
                  iconPosition="left"
                  onClick={fetchSummary}
                  loading={isRefreshing}
                  disabled={isRefreshing || loading}
                >
                  Refresh
                </Button>
              </div>
            </div>

            {error && (
              <div className="mb-4 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning">
                {error}
              </div>
            )}

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <div
                    key={i}
                    className="h-36 rounded-xl border border-border bg-muted/40 animate-spiritual-pulse"
                  />
                ))}
              </div>
            ) : deploymentPoints.length === 0 ? (
              <div className="text-center py-16 bg-card border border-border rounded-xl">
                <Icon name="LayoutGrid" size={48} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No allocation data available</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {deploymentPoints.map((point) => {
                  const allocated = Number(point.allocatedCount) || 0;
                  const required = Number(point.reqCount) || 0;
                  const percent = getFillPercent(allocated, required);
                  const barColor = getStatusColor(allocated, required);
                  const shortfall =
                    point.remainingCount != null
                      ? Math.max(0, Number(point.remainingCount) || 0)
                      : Math.max(0, required - allocated);

                  return (
                    <div
                      key={point._id}
                      className="bg-card border border-border rounded-xl p-4 shadow-spiritual-sm hover:shadow-spiritual-md transition-shadow"
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-primary uppercase tracking-wide truncate mb-1">
                            {point.sector?.name || 'Unassigned Sector'}
                          </p>
                          <h3 className="font-heading font-semibold text-foreground text-sm leading-snug">
                            {point.name || 'Unnamed Point'}
                          </h3>
                        </div>
                        <span
                          className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                            shortfall === 0
                              ? 'bg-success/10 text-success'
                              : 'bg-warning/10 text-warning'
                          }`}
                        >
                          {shortfall === 0 ? 'Full' : `${shortfall} needed`}
                        </span>
                      </div>

                      <div className="flex items-end justify-between mb-2">
                        <div>
                          <p className="text-xs text-muted-foreground mb-0.5">Allocated / Required</p>
                          <p className="font-heading text-2xl font-semibold text-foreground tabular-nums">
                            <span className="text-success">{allocated}</span>
                            <span className="text-muted-foreground text-lg mx-1">/</span>
                            <span className="text-warning">{required}</span>
                          </p>
                        </div>
                        <span className="text-xs font-medium text-muted-foreground tabular-nums">
                          {percent}%
                        </span>
                      </div>

                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                          style={{ width: `${percent}%` }}
                        />
                      </div>

                      {(point.leader1?.name || point.leader2?.name) && (
                        <div className="mt-3 pt-3 border-t border-border space-y-1">
                          {point.leader1?.name && (
                            <p className="text-xs text-muted-foreground truncate">
                              L1: <span className="text-foreground">{point.leader1.name}</span>
                            </p>
                          )}
                          {point.leader2?.name && (
                            <p className="text-xs text-muted-foreground truncate">
                              L2: <span className="text-foreground">{point.leader2.name}</span>
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default AllocationStats;
